import React, { useState } from "react";
import { motion } from "framer-motion";
import styled, { css } from "styled-components";
import { imageTransition, scaleAnimations } from "../../misc/framer";

declare type CoverImageType =
  | "square"
  | "squareSmall"
  | "squareHighlight"
  | "auto";

interface CoverImageProps {
  className?: string;
  variant?: CoverImageType;
  alt?: string;
  src?: string;
  noAnimation?: boolean;
}

export const CoverImage = ({
  className,
  src,
  alt,
  variant = "square",
  noAnimation,
}: CoverImageProps) => {
  const [error, setError] = useState<boolean>(false);
  const onError = () => setError(true);
  return !error ? (
    <Cover
      className={className || ""}
      src={src}
      onError={onError}
      variant={variant}
      alt={alt}
      variants={noAnimation ? undefined : scaleAnimations}
      initial={noAnimation ? undefined : "initial"}
      animate={noAnimation ? undefined : "animate"}
      exit="exit"
      transition={imageTransition}
    />
  ) : (
    <CoverColor
      className={className || ""}
      variant={variant}
      variants={noAnimation ? undefined : scaleAnimations}
      initial={noAnimation ? undefined : "initial"}
      animate={noAnimation ? undefined : "animate"}
      exit="exit"
      transition={imageTransition}
    />
  );
};

const Cover = styled(motion.img)<{ variant: CoverImageType }>`
  object-fit: cover;
  border-radius: 2px;

  ${({ variant }) =>
    variant === "auto" &&
    css`
      width: 100%;
      height: auto;
      aspect-ratio: 1 / 1;
      @media (max-width: ${({ theme }) => `${theme.breakpoints.laptop}px`}) {
        width: 100%;
        height: auto;
      }
      @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
        width: 100%;
        height: auto;
      }
    `}

  ${({ variant }) =>
    variant === "square" &&
    css`
      width: ${({ theme }) => theme.sizes.coverSize};
      max-height: ${({ theme }) => theme.sizes.coverSize};
      @media (max-width: ${({ theme }) => `${theme.breakpoints.laptop}px`}) {
        width: ${({ theme }) => theme.sizes.coverSizeMobile};
        height: ${({ theme }) => theme.sizes.coverSizeMobile};
      }
    `}

  ${({ variant }) =>
    variant === "squareSmall" &&
    css`
      width: ${({ theme }) => theme.sizes.coverSizeSmall};
      max-height: ${({ theme }) => theme.sizes.coverSizeSmall};
    `}

  ${({ variant }) =>
    variant === "squareHighlight" &&
    css`
      width: ${({ theme }) => theme.sizes.coverSizeHighlight};
      max-height: ${({ theme }) => theme.sizes.coverSizeHighlight};

      @media (max-width: ${({ theme }) => `${theme.breakpoints.laptopS}px`}) {
        width: ${({ theme }) => theme.sizes.coverSizeHighlightMobile};
        height: ${({ theme }) => theme.sizes.coverSizeHighlightMobile};
      }
    `}
`;

const CoverColor = styled(motion.div)<{ variant: CoverImageType }>`
  background-color: ${({ theme }) => theme.colors.secondary};
  border-radius: 5px;

  ${({ variant }) =>
    variant === "square" &&
    css`
      width: ${({ theme }) => theme.sizes.coverSize};
      height: ${({ theme }) => theme.sizes.coverSize};
      @media (max-width: ${({ theme }) => `${theme.breakpoints.laptop}px`}) {
        width: ${({ theme }) => theme.sizes.coverSizeMobile};
        height: ${({ theme }) => theme.sizes.coverSizeMobile};
      }
    `}

  ${({ variant }) =>
    variant === "auto" &&
    css`
      width: 100%;
      height: auto;
      aspect-ratio: 1 / 1;
      @media (max-width: ${({ theme }) => `${theme.breakpoints.laptop}px`}) {
        width: ${({ theme }) => theme.sizes.coverSizeMobile};
        height: ${({ theme }) => theme.sizes.coverSizeMobile};
      }
    `}

  ${({ variant }) =>
    variant === "squareSmall" &&
    css`
      width: ${({ theme }) => theme.sizes.coverSizeSmall};
      height: ${({ theme }) => theme.sizes.coverSizeSmall};
    `}

  ${({ variant }) =>
    variant === "squareHighlight" &&
    css`
      width: ${({ theme }) => theme.sizes.coverSizeHighlight};
      height: ${({ theme }) => theme.sizes.coverSizeHighlight};

      @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
        width: ${({ theme }) => theme.sizes.coverSizeHighlightMobile};
        height: ${({ theme }) => theme.sizes.coverSizeHighlightMobile};
      }
    `}
`;
